import { Head, useForm } from "@inertiajs/react"
import { Button, Card, Flex, Heading, Table, Text, TextField } from "@radix-ui/themes"
import { z } from "zod"

import ProjectLayout from "@/layouts/project-layout"
import { projectDashboardPath, projectListPath, projectListsPath } from "@/routes"
import { ProjectSchema, CustomerSchema } from "@/schemas"

const ListSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  created_at: z.string(),
})

const PropsSchema = z.object({
  project: ProjectSchema,
  list: ListSchema,
  customers: z.array(CustomerSchema),
})

type Props = z.infer<typeof PropsSchema>

export default function ListShow(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project, list, customers } = validatedProps
  const { data, setData, patch, processing, errors } = useForm({
    email: "",
  })

  const handleAddCustomer = (e: React.FormEvent) => {
    e.preventDefault()
    patch(projectListPath(project.slug, list.id))
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Custom Lists", href: projectListsPath(project.slug) },
        { title: list.name },
      ]}
    >
      <Head title={`${list.name} - Custom Lists`} />

      <Flex direction="column" gap="6" p="6">
        <Flex direction="column" gap="2">
          <Heading size="6" weight="medium">
            {list.name}
          </Heading>
          {list.description && (
            <Text color="gray" size="2">
              {list.description}
            </Text>
          )}
          <Text color="gray" size="1">
            Created{" "}
            {new Date(list.created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Text>
        </Flex>

        <Flex justify="between" align="center">
          <Heading size="4" weight="medium">
            Customers ({customers.length})
          </Heading>
        </Flex>

        <Card style={{ backgroundColor: "var(--gray-2)", borderColor: "var(--gray-6)" }}>
          <form onSubmit={handleAddCustomer}>
            <Flex
              direction={{ initial: "column", sm: "row" }}
              gap="3"
              align={{ initial: "stretch", sm: "end" }}
            >
              <Flex direction="column" gap="2" style={{ flex: 1 }}>
                <Text size="2" weight="medium">
                  Add customer by email
                </Text>
                <TextField.Root
                  value={data.email}
                  onChange={(e) => setData("email", e.target.value)}
                  placeholder="customer@example.com"
                  disabled={processing}
                  required
                />
                {errors.email && (
                  <Text size="1" color="red">
                    {errors.email}
                  </Text>
                )}
              </Flex>

              <Button type="submit" disabled={processing || !data.email.trim()}>
                {processing ? "Adding..." : "Add customer"}
              </Button>
            </Flex>
          </form>
        </Card>

        <Card>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Events Count</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Customer Since</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {customers.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={3} style={{ textAlign: "center", color: "var(--gray-11)" }}>
                    No customers in this list
                  </Table.Cell>
                </Table.Row>
              ) : (
                customers.map((customer) => (
                  <Table.Row key={customer.id}>
                    <Table.RowHeaderCell>{customer.email}</Table.RowHeaderCell>
                    <Table.Cell>{customer.events_count}</Table.Cell>
                    <Table.Cell>
                      {new Date(customer.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card>
      </Flex>
    </ProjectLayout>
  )
}
